/**
 * Camelot Key Badge Component
 * Displays musical key in Camelot notation with color-coded badge
 */

import { toCamelot, getCamelotColor } from '../../lib/camelot';

export type CamelotKeyBadgeSize = 'sm' | 'md' | 'lg';

export interface CamelotKeyBadgeProps {
  // Option 1: Provide Spotify key and mode
  spotifyKey?: number;
  mode?: 0 | 1;

  // Option 2: Provide Camelot notation directly
  camelot?: string;

  size?: CamelotKeyBadgeSize;
  className?: string;
}

const SIZE_STYLES: Record<CamelotKeyBadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs min-w-[2.5rem]',
  md: 'px-2.5 py-1 text-sm min-w-[3rem]',
  lg: 'px-3 py-1.5 text-base min-w-[3.5rem]',
};

export function CamelotKeyBadge({
  spotifyKey,
  mode,
  camelot,
  size = 'md',
  className = '',
}: CamelotKeyBadgeProps) {
  // Convert to Camelot notation if spotifyKey/mode provided
  let camelotNotation: string;

  if (camelot) {
    camelotNotation = camelot.toUpperCase();
  } else if (spotifyKey !== undefined && mode !== undefined) {
    camelotNotation = toCamelot(spotifyKey, mode);
  } else {
    // No valid input
    return (
      <span
        className={`
          inline-flex items-center justify-center
          rounded-md border font-semibold
          bg-zinc-700/50 text-zinc-400 border-zinc-600/50
          ${SIZE_STYLES[size]}
          ${className}
        `}
      >
        N/A
      </span>
    );
  }

  const colorClasses = getCamelotColor(camelotNotation);

  return (
    <span
      className={`
        inline-flex items-center justify-center
        rounded-md border font-semibold
        ${colorClasses}
        ${SIZE_STYLES[size]}
        ${className}
      `}
      title={`Key: ${camelotNotation}`}
    >
      {camelotNotation}
    </span>
  );
}
